// montecarlo.ts — Monte-Carlo / Renditeband settings, the request-body builder (σ %->bp
// conversion happens HERE, centrally, same pattern as every other %<->bp field in
// convert.ts), and the deterministic "Neu würfeln" seed reroll.
import { MC_DEFAULT_SEED, mulberry32 } from '@etf-cockpit/engine';
import type { ForecastConfig } from '@etf-cockpit/engine';
import { percentToBp } from './convert.js';

export type MonteCarloPathCount = 250 | 500 | 1000;

/** Per-scenario Monte Carlo settings — persisted in localStorage (see storage.ts),
 * NOT the toggle-open state (that's local UI state owned by MonteCarloSection; showing
 * or hiding the section doesn't change what would be simulated). */
export interface MonteCarloSettings {
  /** Annual volatility (σ), in percent (15 = 15% p.a. — a typical global-equity-ETF
   * figure). Converted to ForecastConfig-style basis points only at the API boundary. */
  volatilityPercent: number;
  paths: MonteCarloPathCount;
  seed: number;
}

export function defaultMonteCarloSettings(): MonteCarloSettings {
  return { volatilityPercent: 15, paths: 500, seed: MC_DEFAULT_SEED };
}

/** POST /api/simulate/montecarlo body: the engine's ForecastConfig verbatim, plus the
 * Monte Carlo parameters converted to the wire contract (integer bp). */
export interface MonteCarloRequestBody extends ForecastConfig {
  volatilityBp: number;
  paths: number;
  seed: number;
}

/** The ONE place σ (%) crosses into volatilityBp — reuses convert.ts's percentToBp
 * (the same conversion every other %->bp field in this codebase uses), not a
 * reimplementation. */
export function buildMonteCarloRequestBody(
  config: ForecastConfig,
  settings: MonteCarloSettings,
): MonteCarloRequestBody {
  return {
    ...config,
    volatilityBp: percentToBp(settings.volatilityPercent),
    paths: settings.paths,
    seed: settings.seed,
  };
}

/**
 * Deterministic "Neu würfeln" (reroll) seed: derives a new-looking 32-bit seed from the
 * CURRENT one via the engine's own seeded mulberry32 PRNG. Math.random()/Date.now() are
 * banned for anything feeding the Monte Carlo pipeline (this project requires every
 * random draw to be reproducible from a seed) — rerolling is no exception, it just picks
 * a new seed deterministically rather than reading real entropy. Discards the first draw
 * (closely correlated with small seed values for this PRNG family) and turns the second
 * uniform draw into a 32-bit integer.
 */
export function nextSeed(currentSeed: number): number {
  const rng = mulberry32(currentSeed >>> 0);
  rng();
  return Math.floor(rng() * 0x100000000) >>> 0;
}
